<!-- src/apps/colonel/views/ColonelTenants.vue -->
<!-- secrets-files fork: panel admin para CRUD de tenants -->

<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import {
    useTenantsAdmin,
    type CertStatus,
    type Tenant,
  } from '@/apps/colonel/composables/useTenantsAdmin'

  const {
    tenants,
    isLoading,
    error,
    fetchTenants,
    createTenant,
    updateTenant,
    deleteTenant,
    setSuspended,
    fetchCertStatus,
  } = useTenantsAdmin()

  onMounted(() => fetchTenants())

  // Create form state
  const showCreate = ref(false)
  const createPayload = ref({
    slug: '',
    display_name: '',
    display_domain: '',
    planid: 'free',
  })
  const creating = ref(false)
  const createError = ref<string | null>(null)

  async function handleCreate() {
    creating.value = true
    createError.value = null
    try {
      await createTenant({ ...createPayload.value })
      showCreate.value = false
      createPayload.value = { slug: '', display_name: '', display_domain: '', planid: 'free' }
    } catch {
      createError.value = error.value
    } finally {
      creating.value = false
    }
  }

  // Per-row pending state
  const pendingSlug = ref<string | null>(null)

  async function handleSuspendToggle(t: Tenant) {
    pendingSlug.value = t.slug
    try {
      await setSuspended(t.slug, !t.suspended)
    } finally {
      pendingSlug.value = null
    }
  }

  async function handleDelete(t: Tenant) {
    if (!confirm(`Eliminar tenant "${t.slug}" y sus dominios? Esta acción no borra secrets/files del tenant.`)) {
      return
    }
    pendingSlug.value = t.slug
    try {
      await deleteTenant(t.slug)
    } finally {
      pendingSlug.value = null
    }
  }

  // Cert status panel state
  const certResults = ref<Map<string, CertStatus[]>>(new Map())
  const certPending = ref<Set<string>>(new Set())

  async function handleCheckCert(t: Tenant) {
    certPending.value.add(t.slug)
    try {
      const { certs } = await fetchCertStatus(t.slug)
      certResults.value.set(t.slug, certs)
    } finally {
      certPending.value.delete(t.slug)
    }
  }

  // Plan edit inline
  const editingPlanSlug = ref<string | null>(null)
  const editPlanValue = ref('')

  function startEditPlan(t: Tenant) {
    editingPlanSlug.value = t.slug
    editPlanValue.value = t.planid
  }

  async function commitEditPlan(t: Tenant) {
    if (!editingPlanSlug.value || editPlanValue.value === t.planid) {
      editingPlanSlug.value = null
      return
    }
    pendingSlug.value = t.slug
    try {
      await updateTenant(t.slug, { planid: editPlanValue.value })
    } finally {
      pendingSlug.value = null
      editingPlanSlug.value = null
    }
  }

  function fmtCertExpiry(c: CertStatus): string {
    if (!c.ok) return c.error || 'fail'
    if (c.days_to_expiry === undefined) return '—'
    if (c.days_to_expiry < 0) return `EXPIRED ${-c.days_to_expiry}d ago`
    if (c.days_to_expiry < 14) return `${c.days_to_expiry}d ⚠`
    return `${c.days_to_expiry}d`
  }
</script>

<template>
  <section class="mx-auto max-w-6xl px-4 py-6">
    <header class="mb-4 flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-semibold">Tenants</h1>
        <p class="text-sm text-gray-500">
          CRUD de tenants del fork secrets-files. {{ tenants.length }} registrado(s).
        </p>
      </div>
      <div class="flex gap-2">
        <button
          class="rounded border px-3 py-1 text-sm hover:bg-gray-50"
          :disabled="isLoading"
          @click="fetchTenants"
        >
          {{ isLoading ? 'Cargando…' : 'Refrescar' }}
        </button>
        <button
          class="rounded bg-indigo-600 px-3 py-1 text-sm text-white hover:bg-indigo-700"
          @click="showCreate = !showCreate"
        >
          {{ showCreate ? 'Cancelar' : '+ Nuevo tenant' }}
        </button>
      </div>
    </header>

    <div v-if="showCreate" class="mb-6 rounded border bg-gray-50 p-4">
      <h2 class="mb-3 font-semibold">Crear tenant</h2>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="text-sm">
          <span class="block text-gray-600">Slug</span>
          <input
            v-model="createPayload.slug"
            type="text"
            placeholder="acme-corp"
            class="w-full rounded border px-2 py-1"
            pattern="[a-z0-9][a-z0-9-]{1,30}[a-z0-9]"
            required
          />
          <small class="text-gray-500">3-32, lowercase + dígitos + guiones</small>
        </label>
        <label class="text-sm">
          <span class="block text-gray-600">Display name</span>
          <input
            v-model="createPayload.display_name"
            type="text"
            placeholder="Acme Corp"
            class="w-full rounded border px-2 py-1"
            required
          />
        </label>
        <label class="text-sm">
          <span class="block text-gray-600">Display domain</span>
          <input
            v-model="createPayload.display_domain"
            type="text"
            placeholder="secrets.acme.com"
            class="w-full rounded border px-2 py-1"
            required
          />
        </label>
        <label class="text-sm">
          <span class="block text-gray-600">Plan</span>
          <select v-model="createPayload.planid" class="w-full rounded border px-2 py-1">
            <option value="free">free</option>
            <option value="pro">pro</option>
            <option value="enterprise">enterprise</option>
          </select>
        </label>
      </div>
      <div class="mt-3 flex justify-end gap-2">
        <button class="rounded border px-3 py-1 text-sm" @click="showCreate = false">Cancelar</button>
        <button
          class="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:opacity-50"
          :disabled="creating || !createPayload.slug || !createPayload.display_name || !createPayload.display_domain"
          @click="handleCreate"
        >
          {{ creating ? 'Creando…' : 'Crear' }}
        </button>
      </div>
      <p v-if="createError" class="mt-2 text-sm text-red-600">{{ createError }}</p>
    </div>

    <p v-if="error && !showCreate" class="mb-3 rounded bg-red-50 p-2 text-sm text-red-700">{{ error }}</p>

    <div class="overflow-x-auto rounded border">
      <table class="min-w-full divide-y divide-gray-200 text-sm">
        <thead class="bg-gray-50 text-left">
          <tr>
            <th class="px-3 py-2">Slug</th>
            <th class="px-3 py-2">Nombre</th>
            <th class="px-3 py-2">Plan</th>
            <th class="px-3 py-2">Dominios</th>
            <th class="px-3 py-2">Estado</th>
            <th class="px-3 py-2 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          <tr v-if="!tenants.length && !isLoading">
            <td colspan="6" class="px-3 py-6 text-center text-gray-500">
              No hay tenants. Crea el primero arriba o seedea desde etc/tenants.yaml.
            </td>
          </tr>
          <tr v-for="t in tenants" :key="t.slug" :class="{ 'opacity-50': t.suspended }">
            <td class="px-3 py-2 font-mono">{{ t.slug }}</td>
            <td class="px-3 py-2">{{ t.display_name }}</td>
            <td class="px-3 py-2">
              <span v-if="editingPlanSlug !== t.slug" class="cursor-pointer underline-offset-2 hover:underline" @click="startEditPlan(t)">
                {{ t.planid || '(none)' }}
              </span>
              <span v-else>
                <select
                  v-model="editPlanValue"
                  class="rounded border px-1 py-0.5"
                  @blur="commitEditPlan(t)"
                  @change="commitEditPlan(t)"
                >
                  <option value="free">free</option>
                  <option value="pro">pro</option>
                  <option value="enterprise">enterprise</option>
                </select>
              </span>
            </td>
            <td class="px-3 py-2 font-mono text-xs">
              <div v-for="d in t.domains" :key="d">{{ d }}</div>
              <div v-if="!t.domains.length" class="text-gray-400">—</div>
              <button
                v-if="t.domains.length"
                class="mt-1 text-xs text-indigo-600 underline"
                :disabled="certPending.has(t.slug)"
                @click="handleCheckCert(t)"
              >
                {{ certPending.has(t.slug) ? 'Comprobando…' : 'Cert status' }}
              </button>
              <div v-if="certResults.get(t.slug)" class="mt-1 space-y-0.5 text-xs">
                <div
                  v-for="c in certResults.get(t.slug)"
                  :key="c.display_domain"
                  :class="c.ok ? (c.days_to_expiry !== undefined && c.days_to_expiry < 14 ? 'text-amber-600' : 'text-green-600') : 'text-red-600'"
                >
                  {{ c.display_domain }} → {{ fmtCertExpiry(c) }}
                </div>
              </div>
            </td>
            <td class="px-3 py-2">
              <span v-if="t.suspended" class="rounded bg-red-100 px-2 py-0.5 text-xs text-red-700">SUSPENDED</span>
              <span v-else class="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">active</span>
            </td>
            <td class="px-3 py-2 text-right">
              <button
                class="mr-2 text-xs text-amber-600 underline disabled:opacity-50"
                :disabled="pendingSlug === t.slug"
                @click="handleSuspendToggle(t)"
              >
                {{ t.suspended ? 'Unsuspend' : 'Suspend' }}
              </button>
              <button
                class="text-xs text-red-600 underline disabled:opacity-50"
                :disabled="pendingSlug === t.slug"
                @click="handleDelete(t)"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
